import React, { useEffect } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { pathUtils } from 'utils/pathnameFormatter'
import styled from 'styled-typed'
import { Grid } from 'uiComponents/layout'
import { SecondaryTabsRow } from 'uiComponents/navigation/secondaryTabsRow'
import { LoaderWrapper } from 'uiComponents/loaders'
import { Parkmap } from '../models'
import { CODES_PATHS, ParkMapPagesParams } from '../paths'
import { BaseRouteParams } from 'hocs'
import ParkMapViewerProvider from '../context/mapViewer'
import { useLazyGetParkMapQuery } from '../reduxQuery'
import MapViewer from './mapViewer'
import { useSelectedParkMapContext } from '../context/selectedParkMap'
import { usePrevious } from 'reactUtils'
import { useTranslation } from 'translations'

const TabsContent = styled.div`
    min-height: 500px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`

const ParkMapDetailsContainer: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const { t } = useTranslation()
    const history = useHistory()
    const location = useLocation()
    const params = useParams<BaseRouteParams>()
    const { parkMapId, accountSlug } = useParams<ParkMapPagesParams>()
    const { setSelectedParkMap } = useSelectedParkMapContext()
    const [getParkMap, { isLoading }] = useLazyGetParkMapQuery()
    const prevSlug = usePrevious(accountSlug)

    useEffect(() => {
        if (prevSlug && prevSlug !== accountSlug) {
            history.push(`/account/${accountSlug}/venue/park_map`)
        }
    }, [accountSlug])

    useEffect(() => {
        if (parkMapId && parkMapId !== 'create') {
            getParkMap({ id: parkMapId })
                .unwrap()
                .then((data: Parkmap) => {
                    if (data) {
                        setSelectedParkMap(data)
                    }
                })
        }
    }, [])

    const isActive = (path: string) => location.pathname.includes(path)
    const hrefFor = (path: string) => pathUtils.addQueryString(pathUtils.populateParams(path, params), location.search)
    const createOnClick = (path: string) => () => {
        const formattedPath = pathUtils.addQueryString(pathUtils.populateParams(path, params), location.search)
        history.push(formattedPath)
    }

    return (
        <ParkMapViewerProvider>
            <LoaderWrapper loading={isLoading}>
                <Grid container>
                    <Grid item={6} style={{ padding: '2em' }}>
                        <SecondaryTabsRow
                            tabs={[
                                {
                                    name: t('venue.parkMap.details.tabs.map'),
                                    current: isActive('/details'),
                                    href: hrefFor(CODES_PATHS.fullPaths.mapsAddPage),
                                    onClick: createOnClick(CODES_PATHS.fullPaths.mapsAddPage),
                                },
                                {
                                    name: t('venue.parkMap.details.tabs.style'),
                                    current: isActive('/styles'),
                                    href: hrefFor(CODES_PATHS.fullPaths.stylesAddPage),
                                    onClick: createOnClick(CODES_PATHS.fullPaths.stylesAddPage),
                                },
                                {
                                    name: t('venue.parkMap.details.tabs.pois'),
                                    current: isActive('/cat_poi'),
                                    href: hrefFor(CODES_PATHS.fullPaths.poisAddPage),
                                    onClick: createOnClick(CODES_PATHS.fullPaths.poisAddPage),
                                },
                            ]}
                        />
                        <TabsContent>{children}</TabsContent>
                    </Grid>
                    <Grid item={6} style={{ paddingBottom: 0, paddingLeft: 0, position: 'relative' }}>
                        <MapViewer />
                    </Grid>
                </Grid>
            </LoaderWrapper>
        </ParkMapViewerProvider>
    )
}

export default ParkMapDetailsContainer
